import React, { useEffect } from "react";
import PropTypes from "prop-types";
import {
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Stack,
  Divider,
  FormControlLabel,
  Switch,
  Typography,
} from "@mui/material";
import { useForm, Controller } from "react-hook-form";
import dayjs from "dayjs";
import {
  useCreateEventMutation,
  useUpdateEventMutation,
} from "../../features/events/eventsApiSlice";
import { useDispatch } from "react-redux";
import { setDialog } from "../../features/dialog/dialogSlice";

export default function TestingEventFormDialog({
  initialEvent,
  onSaved,
  onClose,
}) {
  const dispatch = useDispatch();
  const isEdit = Boolean(initialEvent);
  const [createEvent, { isLoading: isCreating }] = useCreateEventMutation();
  const [updateEvent, { isLoading: isUpdating }] = useUpdateEventMutation();

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      eventName: "",
      eventDateBegin: "",
      eventDateEnd: "",
      locationName: "",
      city: "",
      zip: "",
      street: "",
      allowSelfRegistration: false,
    },
  });

  useEffect(() => {
    if (initialEvent) {
      reset({
        eventName: initialEvent.eventName || "",
        eventDateBegin: initialEvent.eventDateBegin
          ? dayjs(initialEvent.eventDateBegin).format("YYYY-MM-DDTHH:mm")
          : "",
        eventDateEnd: initialEvent.eventDateEnd
          ? dayjs(initialEvent.eventDateEnd).format("YYYY-MM-DDTHH:mm")
          : "",
        locationName: initialEvent.locationName || "",
        city: initialEvent.city || "",
        zip: initialEvent.zip || "",
        street: initialEvent.street || "",
        allowSelfRegistration:
          typeof initialEvent.allowSelfRegistration === "boolean"
            ? Boolean(initialEvent.allowSelfRegistration)
            : false,
      });
    } else {
      reset({
        eventName: "",
        eventDateBegin: "",
        eventDateEnd: "",
        locationName: "",
        city: "",
        zip: "",
        street: "",
        allowSelfRegistration: false,
      });
    }
  }, [initialEvent, reset]);

  const onSubmit = async (data) => {
    try {
      const payload = {
        ...data,
        eventType: "TESTING",
        eventDateBegin: data.eventDateBegin || undefined,
        eventDateEnd: data.eventDateEnd || undefined,
        allowSelfRegistration: Boolean(data.allowSelfRegistration),
      };

      if (isEdit) {
        await updateEvent({
          id: initialEvent._id || initialEvent.id,
          ...payload,
        }).unwrap();
        dispatch(
          setDialog({
            type: "SUCCESS",
            title: "Prüfung gespeichert",
            data: { successText: "Änderungen wurden gespeichert." },
          })
        );
      } else {
        await createEvent(payload).unwrap();
        dispatch(
          setDialog({
            type: "SUCCESS",
            title: "Prüfung erstellt",
            data: { successText: "Die Prüfung wurde angelegt." },
          })
        );
      }
      onSaved?.();
    } catch (e) {
      dispatch(
        setDialog({
          type: "ERROR",
          title: "Fehler",
          data: { errorText: e?.data?.message || "Speichern fehlgeschlagen." },
        })
      );
    }
  };

  return (
    <>
      <DialogTitle>
        {isEdit ? "Prüfung bearbeiten" : "Neue Prüfung anlegen"}
      </DialogTitle>
      <DialogContent dividers>
        <form onSubmit={handleSubmit(onSubmit)} className="form">
          <Stack spacing={2}>
            <Controller
              name="eventName"
              control={control}
              rules={{ required: "Bitte einen Namen für die Prüfung angeben." }}
              render={({ field }) => (
                <TextField
                  label="Name der Prüfung"
                  value={field.value}
                  onChange={field.onChange}
                  error={!!errors.eventName}
                  helperText={errors.eventName?.message}
                  required
                />
              )}
            />

            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <Controller
                name="eventDateBegin"
                control={control}
                render={({ field }) => (
                  <TextField
                    label="Beginn"
                    type="datetime-local"
                    value={field.value}
                    onChange={field.onChange}
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                  />
                )}
              />
              <Controller
                name="eventDateEnd"
                control={control}
                render={({ field }) => (
                  <TextField
                    label="Ende (optional)"
                    type="datetime-local"
                    value={field.value}
                    onChange={field.onChange}
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                  />
                )}
              />
            </Stack>

            <Divider />

            <Controller
              name="locationName"
              control={control}
              render={({ field }) => (
                <TextField
                  label="Ort / Halle"
                  value={field.value}
                  onChange={field.onChange}
                />
              )}
            />

            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <Controller
                name="city"
                control={control}
                render={({ field }) => (
                  <TextField
                    label="Stadt"
                    value={field.value}
                    onChange={field.onChange}
                    fullWidth
                  />
                )}
              />
              <Controller
                name="zip"
                control={control}
                render={({ field }) => (
                  <TextField
                    label="PLZ"
                    value={field.value}
                    onChange={field.onChange}
                    fullWidth
                  />
                )}
              />
            </Stack>

            <Controller
              name="street"
              control={control}
              render={({ field }) => (
                <TextField
                  label="Straße"
                  value={field.value}
                  onChange={field.onChange}
                />
              )}
            />

            <Divider />
            <Controller
              name="allowSelfRegistration"
              control={control}
              render={({ field: { value, onChange } }) => (
                <FormControlLabel
                  control={
                    <Switch
                      checked={Boolean(value)}
                      onChange={(event) => onChange(event.target.checked)}
                      color="secondary"
                    />
                  }
                  label="Mitglieder dürfen sich selbst anmelden"
                />
              )}
            />
            <Typography variant="caption" sx={{ mt: -1, color: "text.secondary" }}>
              Standard für neue Prüfungen: deaktiviert. Dann können nur Admin, Editor oder Trainer Mitglieder hinzufügen.
            </Typography>
          </Stack>

          <DialogActions sx={{ px: 0, mt: 2 }}>
            <Button onClick={onClose}>Abbrechen</Button>
            <Button
              type="submit"
              variant="contained"
              color="secondary"
              disabled={isCreating || isUpdating}
            >
              {isEdit ? "Speichern" : "Anlegen"}
            </Button>
          </DialogActions>
        </form>
      </DialogContent>
    </>
  );
}

TestingEventFormDialog.propTypes = {
  initialEvent: PropTypes.object,
  onSaved: PropTypes.func,
  onClose: PropTypes.func,
};
